import React, { useState } from 'react';
import { Search, ShoppingCart, Heart, User } from 'lucide-react';

export default function SearchBarDesigns() {
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('All Categories');

  const categories = [
    'All Categories',
    'Electronics',
    'Fashion',
    'Home & Garden',
    'Sports',
    'Books',
    'Toys',
    'Beauty'
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Design 1: Orange Accent Search Bar */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-gray-800 p-6">Design 1: Orange Accent Style</h2>
        
        <div className="bg-white shadow-md">
          {/* Logo and Contact Row */}
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xl">R</span>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">Revo</h1>
                  <p className="text-xs text-gray-500">Shop all you want</p>
                </div>
              </div>
              
              <div className="hidden md:flex items-center gap-2 text-sm">
                <span className="text-orange-500">📞 Call Us Now:</span>
                <span className="font-semibold text-gray-800">(123) 456-6666</span>
                <span className="text-gray-400 mx-2">|</span>
                <span className="text-gray-600">Email: contact@example.com</span>
              </div>

              <div className="flex items-center gap-3">
                <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors relative">
                  <Heart className="w-6 h-6 text-gray-700" />
                  <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">2</span>
                </button>
                <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors relative">
                  <ShoppingCart className="w-6 h-6 text-gray-700" />
                  <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">3</span>
                </button>
              </div>
            </div>
          </div>

          {/* Search Bar Section */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 py-4">
            <div className="max-w-7xl mx-auto px-4">
              <div className="flex flex-col md:flex-row gap-3 items-stretch">
                {/* All Departments Button - Hidden on Mobile */}
                <button className="hidden md:flex bg-orange-700 text-white px-6 py-3 rounded-lg font-semibold items-center gap-2 hover:bg-orange-800 transition-colors whitespace-nowrap">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                  ALL DEPARTMENTS
                </button>

                {/* Search Input */}
                <div className="flex-1 flex">
                  <select 
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="bg-white border-r border-gray-200 px-4 py-3 rounded-l-lg text-gray-700 font-medium focus:outline-none cursor-pointer hidden md:block"
                  >
                    {categories.map((cat, idx) => (
                      <option key={idx} value={cat}>{cat}</option>
                    ))}
                  </select>
                  
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-1 px-4 py-3 md:rounded-none rounded-l-lg focus:outline-none text-gray-700"
                  />
                  
                  <button className="bg-orange-700 hover:bg-orange-800 text-white px-6 md:px-8 rounded-r-lg transition-colors flex items-center justify-center">
                    <Search className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Design 2: Blue Modern Search Bar */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-gray-800 p-6">Design 2: Blue Modern Style</h2>
        
        <div className="bg-white shadow-md">
          {/* Top Info Bar */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-2">
            <div className="max-w-7xl mx-auto px-4 flex flex-wrap items-center justify-between text-sm">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Official Store Shop
                </span>
                <span className="hidden md:inline">|</span>
                <span className="hidden md:flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Same-day dispatch
                </span>
              </div>
              <div className="flex items-center gap-4">
                <button className="flex items-center gap-1 hover:text-blue-200 transition-colors">
                  <User className="w-4 h-4" />
                  <span>My Account</span>
                </button>
                <span>|</span>
                <select className="bg-transparent border-none focus:outline-none cursor-pointer text-white">
                  <option className="text-gray-800">USD $</option>
                  <option className="text-gray-800">EUR €</option>
                  <option className="text-gray-800">GBP £</option>
                </select>
              </div>
            </div>
          </div>

          {/* Main Header with Logo and Search */}
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex flex-col md:flex-row items-center gap-4">
              {/* Logo */}
              <div className="flex items-center gap-2 md:w-48">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-2xl">R</span>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">Revo</h1>
                </div>
              </div>

              {/* Search Bar - Takes Full Width on Mobile */}
              <div className="flex-1 w-full">
                <div className="flex items-center bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200 focus-within:border-blue-500 transition-colors">
                  <select 
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="bg-white border-r border-gray-200 px-3 md:px-4 py-3 text-gray-700 font-medium focus:outline-none cursor-pointer text-sm md:text-base"
                  >
                    {categories.map((cat, idx) => (
                      <option key={idx} value={cat}>{cat}</option>
                    ))}
                  </select>
                  
                  <input
                    type="text"
                    placeholder="Search for products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-1 px-3 md:px-4 py-3 bg-gray-100 focus:outline-none text-gray-700 text-sm md:text-base"
                  />
                  
                  <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 md:px-6 py-3 transition-colors">
                    <Search className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Contact Info and Cart - Hidden on Mobile */}
              <div className="hidden lg:flex items-center gap-4 text-sm">
                <div className="text-right">
                  <p className="text-blue-600 font-semibold">📞 Call Us Now:</p>
                  <p className="text-gray-800 font-bold">(123) 456-6666</p>
                </div>
                <div className="flex items-center gap-2">
                  <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors relative">
                    <Heart className="w-6 h-6 text-gray-700" />
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">4</span>
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors relative">
                    <ShoppingCart className="w-6 h-6 text-gray-700" />
                    <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">7</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Demo Content */}
      <div className="p-8">
        <div className="max-w-7xl mx-auto bg-white rounded-2xl p-8 shadow-lg">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Design Features</h3>
          <div className="grid md:grid-cols-2 gap-6 text-sm text-gray-600">
            <div>
              <h4 className="font-bold text-gray-800 mb-2">Design 1: Orange Accent</h4>
              <ul className="space-y-1 list-disc list-inside">
                <li>Bold orange gradient background</li>
                <li>Visible search bar on all devices</li>
                <li>Category dropdown (hidden on mobile)</li>
                <li>All Departments button (desktop only)</li>
                <li>Clean, modern e-commerce feel</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-gray-800 mb-2">Design 2: Blue Modern</h4>
              <ul className="space-y-1 list-disc list-inside">
                <li>Professional blue color scheme</li>
                <li>Always visible search with category filter</li>
                <li>Top info bar with features</li>
                <li>Full-width search on mobile</li>
                <li>Trust badges and account options</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}